/**
 * useServiceOrderServices - Serviços do Negócio na O.S. (aba Itens).
 *
 * Leitura e gravação 100% via RPC; `.call(supabase)` preserva o `this` do cliente.
 *
 * RELEASE: 2026-10-18 v1.1.0 - BR-OS-054: mutação `manage` (adicionar/alterar/remover pelo operador).
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { ServiceOrderServicesData } from '@/lib/serviceOrders/serviceItems';

const rpc = (fn: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => Promise<{ data: any; error: any }>).call(supabase, fn, args);

const KEY = 'service-order-services';

export function useServiceOrderServices(serviceOrderId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const hint = currentTenant?.id ?? null;
  return useQuery({
    queryKey: [KEY, hint, serviceOrderId],
    enabled: !!serviceOrderId && !!hint,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_services', { p_id: serviceOrderId, p_tenant_hint: hint });
      if (error) throw error;
      return data as ServiceOrderServicesData;
    },
  });
}

/** Ações da aba Itens; todas recarregam itens e andamentos. */
export function useServiceOrderServiceMutations(serviceOrderId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const hint = currentTenant?.id ?? null;
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: [KEY] });
    qc.invalidateQueries({ queryKey: ['service-orders'] });
  };
  const call = async (fn: string, args: Record<string, unknown>) => {
    const { data, error } = await rpc(fn, { ...args, p_tenant_hint: hint });
    if (error) throw error;
    return data;
  };
  return {
    setDeal: useMutation({ mutationFn: (a: { orderId: string; dealId: string | null }) => call('set_service_order_deal', { p_id: a.orderId, p_deal_id: a.dealId }), onSuccess: refresh }),
    sync: useMutation({ mutationFn: () => call('sync_service_order_deal_services', { p_id: serviceOrderId }), onSuccess: refresh }),
    saveResults: useMutation({ mutationFn: (results: unknown[]) => call('save_service_order_service_results', { p_id: serviceOrderId, p_results: results }), onSuccess: refresh }),
    addField: useMutation({
      mutationFn: (a: { productId: string; quantity: number; notes: string }) =>
        call('add_service_order_field_service', { p_id: serviceOrderId, p_product_id: a.productId, p_quantity: a.quantity, p_notes: a.notes.trim() || null }),
      onSuccess: refresh,
    }),
    // BR-OS-054: edição dos itens pelo operador (nunca altera o negócio)
    manage: useMutation({
      mutationFn: (a: { action: 'add' | 'update' | 'remove'; payload: Record<string, unknown> }) =>
        call('manage_service_order_services', { p_order_id: serviceOrderId, p_action: a.action, p_payload: a.payload }),
      onSuccess: refresh,
    }),
    review: useMutation({
      mutationFn: (a: { serviceId: string; decision: 'approved' | 'rejected'; reason?: string }) =>
        call('review_service_order_field_service', { p_service_id: a.serviceId, p_decision: a.decision, p_reason: a.reason?.trim() || null }),
      onSuccess: refresh,
    }),
  };
}

/** Serviços do catálogo (para "Adicionar serviço"). */
export function useServiceCatalogProducts(enabled: boolean) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: [KEY, 'products', currentTenant?.id],
    enabled: enabled && !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await rpc('list_service_order_service_products', { p_search: null, p_tenant_hint: currentTenant?.id });
      if (error) throw error;
      return (data ?? []) as { id: string; name: string; sku: string | null }[];
    },
  });
}

/** Negócios do cliente para o campo "Negócio" da O.S. */
export function useServiceOrderDealOptions(customerId: string | null | undefined) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: [KEY, 'deals', currentTenant?.id, customerId],
    enabled: !!customerId && !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await rpc('list_service_order_deal_options', { p_customer_id: customerId, p_tenant_hint: currentTenant?.id });
      if (error) throw error;
      return (data ?? []) as { id: string; code: string; title: string; status: string; services: number }[];
    },
  });
}
